import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import {
  DiscountGroupType,
  DiscountMinRequirementType,
  DiscountProvider,
  DiscountValueType,
  ExpirationType,
} from "../discount";

const expireAfterDaysSchema = z.object({
  expirationType: z.literal(ExpirationType.EXPIRATION_DAYS),
  days: z.number(),
});
const expireAfterDateSchema = z.object({
  expirationType: z.literal(ExpirationType.DATE),
  date: z.date(),
});
const expireNeverSchema = z.object({
  expirationType: z.literal(ExpirationType.NEVER),
});
const expireAfterDeliverySchema = z.object({
  expirationType: z.literal(ExpirationType.DELIVERY),
});

export const expirationSchema = z.discriminatedUnion("expirationType", [
  expireAfterDaysSchema,
  expireAfterDateSchema,
  expireNeverSchema,
  expireAfterDeliverySchema,
]);

// Define schemas for discount settings types
const combinesWithSchema = z.object({
  orderDiscounts: z.boolean().optional(),
  productDiscounts: z.boolean().optional(),
  shippingDiscount: z.boolean().optional(),
});

export enum DiscountSettingsType {
  SHOPIFY_BASIC = "SHOPIFY_BASIC",
}

export type BasicShopifyDiscountConfiguration = z.infer<
  typeof basicShopifyDiscountSchema
>;
const basicShopifyDiscountSchema = z.object({
  settingsType: z.literal(DiscountSettingsType.SHOPIFY_BASIC),
  discountValueType: z.enum(DiscountValueType),
  discountValueAmount: z.number(),
  combinesWith: combinesWithSchema,
  excludeItemsOnSale: z.boolean().optional(),
  appliesToCollections: z.array(z.string()).optional(),
  appliesToProducts: z.array(z.string()).optional(),
  endsAt: z.date().optional(),
  minimumRequirement: z
    .object({ type: z.enum(DiscountMinRequirementType), minimum: z.number() })
    .optional(),
  customerEligibility: z.enum(["all", "segments", "specific"]).optional(),
  limitToCustomers: z.array(z.string()).optional(),
  limitToSegments: z.array(z.string()).optional(),
  totalUsageLimit: z.number().positive().optional(),
  appliesOncePerCustomer: z.boolean().optional(),
  startsAt: z.date().optional(),
});

// Using discriminatedUnion for future extensibility
export const discountSettingsSchema = z.discriminatedUnion("settingsType", [
  basicShopifyDiscountSchema,
]);
export type DiscountSettings = z.infer<typeof discountSettingsSchema>;

export const databaseDiscountSchema = z.object({
  title: z.string(),
  provider: z.enum(DiscountProvider),
  groupType: z.enum(DiscountGroupType),
  discountId: z.string(),
  code: z.string(),
  expiration: expirationSchema,
  discountSettings: discountSettingsSchema.optional(),
});

export type RedoDatabaseDiscount = z.infer<typeof DiscountDbParser>;
export const DiscountDbParser = z.intersection(
  databaseDiscountSchema,
  z.object({ _id: zExt.objectId(), teamId: zExt.objectId() }),
);
